import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { AdminUsersService } from '../../../core/admin/admin-users.service';
import {
  AdminRole,
  AdminUser,
  MCP_TIER_LABELS,
  OPS_TIER_LABELS,
  OPS_TIER_OPTIONS,
  OpsTier,
} from '../../../core/admin/admin-users.types';
import { AuthService } from '../../../core/auth/auth.service';
import { withErrorRef } from '../../../core/error-ref';
import { IconComponent } from '../../../shared/icon/icon';
import {
  McpAccessModalComponent,
  McpAccessModalData,
} from './mcp-access-modal/mcp-access-modal';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

/** Client-side row filters at the top of the Users page. */
type StatusFilter = 'all' | 'active' | 'inactive' | 'unverified';
type AccessFilter = 'all' | 'ops' | 'mcp' | 'admin';

@Component({
  selector: 'app-admin-users',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    DatePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    IconComponent,
    DashPipe,
  ],
  templateUrl: './admin-users.html',
  styleUrl: './admin-users.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminUsersComponent implements OnInit {
  private readonly service = inject(AdminUsersService);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly tierLabels = OPS_TIER_LABELS;
  protected readonly tierOptions = OPS_TIER_OPTIONS;
  protected readonly mcpLabels = MCP_TIER_LABELS;

  protected readonly users = signal<AdminUser[]>([]);
  protected readonly roles = signal<AdminRole[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal(false);
  protected readonly searchTerm = signal('');
  protected readonly hasSearch = signal(false);

  // Client-side filters applied on top of the server search.
  protected readonly statusFilter = signal<StatusFilter>('all');
  protected readonly accessFilter = signal<AccessFilter>('all');

  /** The users actually rendered: server list narrowed by the row filters. */
  protected readonly visibleUsers = computed(() => {
    const status = this.statusFilter();
    const access = this.accessFilter();
    return this.users().filter((u) => {
      const statusOk =
        status === 'all' ||
        (status === 'active' && u.is_active) ||
        (status === 'inactive' && !u.is_active) ||
        (status === 'unverified' && !u.email_verified_at);
      const accessOk =
        access === 'all' ||
        (access === 'ops' && !!u.ops_tier) ||
        (access === 'mcp' && !!u.mcp_access_tier) ||
        (access === 'admin' &&
          u.system_roles.some((r) => r.slug === 'capx-admin'));
      return statusOk && accessOk;
    });
  });

  /** True when a row filter is hiding some of the loaded users. */
  protected readonly filtersActive = computed(
    () => this.statusFilter() !== 'all' || this.accessFilter() !== 'all',
  );

  // Which user row is expanded for editing (id), and the add-user panel toggle.
  protected readonly expandedId = signal<string | null>(null);
  protected readonly addOpen = signal(false);
  protected readonly creating = signal(false);
  // Per-row in-flight guard so toggles don't double-fire.
  protected readonly savingId = signal<string | null>(null);

  protected readonly createForm = this.fb.group({
    first_name: ['', [Validators.required, Validators.maxLength(150)]],
    last_name: ['', [Validators.required, Validators.maxLength(150)]],
    email: ['', [Validators.required, Validators.email]],
    ops_tier: this.fb.control<OpsTier | null>(null),
    is_credit_committee_member: [false],
    notify_new_applications: [false],
    role_slugs: this.fb.control<string[]>([]),
  });

  // Live view of the create form so the template can warn when a tier is picked
  // without the Ops role (the tier would be inert). `toSignal` tracks valueChanges.
  private readonly createValue = toSignal(this.createForm.valueChanges, {
    initialValue: this.createForm.getRawValue(),
  });
  protected readonly createTierWithoutOps = computed(() => {
    const v = this.createValue();
    return !!v.ops_tier && !(v.role_slugs ?? []).includes('ops');
  });

  ngOnInit(): void {
    this.loadRoles();
    this.load();
  }

  /** The signed-in admin's id — used to lock self-deactivation / self-admin-revoke. */
  protected get currentUserId(): string | null {
    return this.auth.currentUser()?.id ?? null;
  }

  protected load(search = this.searchTerm()): void {
    this.loading.set(true);
    this.error.set(false);
    this.hasSearch.set(!!search.trim());
    this.service
      .list(search.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.users.set(res.users);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  private loadRoles(): void {
    this.service
      .roles()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.roles.set(res.roles);
          this.cdr.markForCheck();
        },
        error: () => {
          /* roles picker just stays empty; non-fatal */
        },
      });
  }

  protected onSearch(value: string): void {
    this.searchTerm.set(value);
    this.load(value);
  }

  protected clearSearch(): void {
    this.searchTerm.set('');
    this.load('');
  }

  protected setStatusFilter(value: StatusFilter): void {
    this.statusFilter.set(value);
  }

  protected setAccessFilter(value: AccessFilter): void {
    this.accessFilter.set(value);
  }

  protected resetFilters(): void {
    this.statusFilter.set('all');
    this.accessFilter.set('all');
  }

  /** Open the MCP-access modal; on a saved change, swap the updated row in. */
  protected openMcpAccess(user: AdminUser): void {
    const ref = this.dialog.open<
      McpAccessModalComponent,
      McpAccessModalData,
      AdminUser | undefined
    >(McpAccessModalComponent, {
      data: { user },
      width: '90vw',
      maxWidth: '560px',
      autoFocus: false,
    });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((updated) => {
        if (updated) {
          this.replaceUser(updated);
          this.snackBar.open(
            updated.mcp_access_tier
              ? `MCP access set to ${updated.mcp_access_label}.`
              : 'MCP access revoked.',
            'Dismiss',
            { duration: 5000 },
          );
        }
      });
  }

  protected toggleRow(id: string): void {
    this.expandedId.set(this.expandedId() === id ? null : id);
  }

  protected toggleAdd(): void {
    this.addOpen.set(!this.addOpen());
    if (!this.addOpen()) {
      this.createForm.reset({
        first_name: '',
        last_name: '',
        email: '',
        ops_tier: null,
        is_credit_committee_member: false,
        notify_new_applications: false,
        role_slugs: [],
      });
    }
  }

  protected submitCreate(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    this.creating.set(true);
    const v = this.createForm.getRawValue();
    this.service
      .create({
        first_name: v.first_name!,
        last_name: v.last_name!,
        email: v.email!,
        ops_tier: v.ops_tier,
        is_credit_committee_member: v.is_credit_committee_member!,
        notify_new_applications: v.notify_new_applications!,
        role_slugs: v.role_slugs ?? [],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.creating.set(false);
          this.toggleAdd();
          this.snackBar.open(
            `Invite sent to ${user.email}.`,
            'Dismiss',
            { duration: 5000 },
          );
          this.load();
        },
        error: (err: HttpErrorResponse) => {
          this.creating.set(false);
          const detail = err.error?.detail;
          const msg =
            detail?.email?.[0] ||
            detail?.role_slugs?.[0] ||
            withErrorRef('Could not create the user.', err);
          this.snackBar.open(msg, 'Dismiss', { duration: 6000 });
          this.cdr.markForCheck();
        },
      });
  }

  // --- Per-user authority mutations -----------------------------------------

  private replaceUser(updated: AdminUser): void {
    this.users.set(
      this.users().map((u) => (u.id === updated.id ? updated : u)),
    );
    this.cdr.markForCheck();
  }

  protected setOpsTier(user: AdminUser, tier: OpsTier | null): void {
    this.savingId.set(user.id);
    this.service
      .update(user.id, { ops_tier: tier })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (u) => {
          this.savingId.set(null);
          this.replaceUser(u);
        },
        error: (err: HttpErrorResponse) =>
          this.failSave(withErrorRef('Could not update the authority tier.', err)),
      });
  }

  protected setCcMember(user: AdminUser, value: boolean): void {
    this.savingId.set(user.id);
    this.service
      .update(user.id, { is_credit_committee_member: value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (u) => {
          this.savingId.set(null);
          this.replaceUser(u);
        },
        error: (err: HttpErrorResponse) =>
          this.failSave(withErrorRef('Could not update the Credit Committee flag.', err)),
      });
  }

  protected setNotifyNewApplications(user: AdminUser, value: boolean): void {
    this.savingId.set(user.id);
    this.service
      .update(user.id, { notify_new_applications: value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (u) => {
          this.savingId.set(null);
          this.replaceUser(u);
        },
        error: (err: HttpErrorResponse) =>
          this.failSave(withErrorRef('Could not update the new-application notification.', err)),
      });
  }

  protected setActive(user: AdminUser, value: boolean): void {
    this.savingId.set(user.id);
    this.service
      .update(user.id, { is_active: value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (u) => {
          this.savingId.set(null);
          this.replaceUser(u);
        },
        error: (err: HttpErrorResponse) => {
          const msg =
            err.error?.code === 'SELF_DEACTIVATION'
              ? 'You cannot deactivate your own account.'
              : withErrorRef('Could not change the active state.', err);
          this.failSave(msg);
        },
      });
  }

  protected hasRole(user: AdminUser, slug: string): boolean {
    return user.system_roles.some((r) => r.slug === slug);
  }

  /**
   * True if the user holds the `ops` SYSTEM role — the "can reach the Ops area"
   * signal. The authority tier only takes effect for such users: a tier set on a
   * non-Ops account is inert. Drives the coupling warning on the tier control.
   */
  protected hasOpsRole(user: AdminUser): boolean {
    return this.hasRole(user, 'ops');
  }

  protected toggleRole(user: AdminUser, role: AdminRole, checked: boolean): void {
    this.savingId.set(user.id);
    const call = checked
      ? this.service.assignRole(user.id, role.slug)
      : this.service.revokeRole(user.id, role.slug);
    call.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (u) => {
        this.savingId.set(null);
        this.replaceUser(u);
      },
      error: (err: HttpErrorResponse) => {
        const msg =
          err.error?.code === 'SELF_ADMIN_REVOKE'
            ? 'You cannot revoke your own admin role.'
            : withErrorRef('Could not update the role.', err);
        this.failSave(msg);
      },
    });
  }

  /** True when this role checkbox must stay disabled (self-admin lock-out). */
  protected roleLocked(user: AdminUser, role: AdminRole): boolean {
    return (
      role.slug === 'capx-admin' &&
      user.id === this.currentUserId &&
      this.hasRole(user, 'capx-admin')
    );
  }

  private failSave(message: string): void {
    this.savingId.set(null);
    this.snackBar.open(message, 'Dismiss', { duration: 6000 });
    // Reload to discard any optimistic UI drift from the failed mutation.
    this.load();
  }
}
